import { useState, type FormEvent } from 'react';
import { useOp } from '../../api.ts';
import { runWrite } from '../../ui/changes.ts';
import { Drawer, Loading } from '../../ui/kit.tsx';
import { useToast } from '../../ui/toasts.tsx';
import type { AiMode, OverviewOrganization, SiteOptions } from './types.ts';
import { AI_MODE_LABELS, CAPABILITY_LABELS } from './ui.tsx';

/** Organization settings: name, slug, status, "no AI content", the AI ceiling for its sites and the AI budget. */
export function OrgDrawer({ org, onClose }: { org: OverviewOrganization; onClose: () => void }) {
  const toast = useToast();
  const options = useOp<SiteOptions>('admin.sites.options');
  const [name, setName] = useState(org.name);
  const [slug, setSlug] = useState(org.slug);
  const [status, setStatus] = useState(org.status);
  const [noAi, setNoAi] = useState(org.noAiContent);
  const [ceilingDefault, setCeilingDefault] = useState<AiMode | ''>(org.aiCeiling?.default ?? '');
  const [ceiling, setCeiling] = useState<Record<string, AiMode | ''>>(org.aiCeiling?.capabilities ?? {});
  const [budget, setBudget] = useState(org.monthlyAiBudgetCents === null ? '' : String(org.monthlyAiBudgetCents / 100));
  const [saving, setSaving] = useState(false);

  const save = async (event: FormEvent) => {
    event.preventDefault();
    setSaving(true);
    const capabilities = Object.fromEntries(Object.entries(ceiling).filter(([, v]) => v !== ''));
    const aiCeiling = ceilingDefault === '' && Object.keys(capabilities).length === 0 ? null : { ...(ceilingDefault ? { default: ceilingDefault } : {}), capabilities };
    const result = await runWrite<{ sitesAboveCeiling: { name: string; capabilities: string[] }[] }>(
      toast,
      'admin.orgs.update',
      { ref: org.ref, name, slug, status, noAiContent: noAi, aiCeiling, monthlyAiBudgetCents: budget === '' ? null : Math.round(Number(budget) * 100) },
      (r) => (r.sitesAboveCeiling.length ? `Saved. Capped by the new ceiling: ${r.sitesAboveCeiling.map((s) => s.name).join(', ')}` : 'Organization saved'),
    );
    setSaving(false);
    if (result) onClose();
  };

  return (
    <Drawer title={`${org.name}: settings`} onClose={onClose}>
      <form onSubmit={save} aria-label="Organization settings">
        <fieldset disabled={!org.canAdmin}>
          <div className="form-grid">
            <label>
              Name
              <input required minLength={2} value={name} onChange={(e) => setName(e.target.value)} />
            </label>
            <label>
              Slug
              <input required value={slug} pattern="[a-z0-9]+(-[a-z0-9]+)*" onChange={(e) => setSlug(e.target.value)} />
            </label>
            <label>
              Status
              <select value={status} onChange={(e) => setStatus(e.target.value)}>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </label>
            <label>
              Monthly AI budget
              <input type="number" min={0} step="1" value={budget} placeholder="no limit" onChange={(e) => setBudget(e.target.value)} />
            </label>
            <label className="inline-check span-2">
              <input type="checkbox" checked={noAi} onChange={(e) => setNoAi(e.target.checked)} /> No AI content: AI writes nothing for this organization (checks keep running)
            </label>
          </div>
          <h3 className="section-title">AI ceiling for all sites</h3>
          <p className="muted small">Sites can choose less, never more. Lowering the ceiling caps sites at once; their own setting is kept.</p>
          {!options.data ? (
            <Loading />
          ) : (
            <>
              <label className="inline-field">
                All capabilities
                <select value={ceilingDefault} disabled={noAi} onChange={(e) => setCeilingDefault(e.target.value as AiMode | '')}>
                  <option value="">No limit (automatic allowed)</option>
                  {options.data.aiModes.map((m) => (
                    <option key={m} value={m}>
                      {AI_MODE_LABELS[m]}
                    </option>
                  ))}
                </select>
              </label>
              <table className="table compact">
                <tbody>
                  {options.data.aiCapabilities.map((capability) => (
                    <tr key={capability}>
                      <th scope="row">{CAPABILITY_LABELS[capability] ?? capability}</th>
                      <td>
                        <select
                          aria-label={`Ceiling for ${CAPABILITY_LABELS[capability] ?? capability}`}
                          value={ceiling[capability] ?? ''}
                          disabled={noAi}
                          onChange={(e) => setCeiling((c) => ({ ...c, [capability]: e.target.value as AiMode | '' }))}
                        >
                          <option value="">Same as all</option>
                          {options.data!.aiModes.map((m) => (
                            <option key={m} value={m}>
                              {AI_MODE_LABELS[m]}
                            </option>
                          ))}
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </fieldset>
        {org.canAdmin ? (
          <div className="form-actions sticky-actions">
            <button type="submit" className="btn btn-primary" disabled={saving}>
              Save organization
            </button>
          </div>
        ) : null}
      </form>
    </Drawer>
  );
}
